import {
  CareerProps,
  ContactInfo,
  ContactSocialMedia,
  FinancialSupport,
  InstituteProps,
  facultyProps,
} from "@/types";

export const hasContactInfoData = (data: Partial<ContactInfo> | undefined): boolean => {
  if (!data) return false;
  const hasEmail = Boolean(data.contactEmail && data.contactEmail.trim() !== "");
  const hasPhone = Boolean(data.contactPhone && data.contactPhone.toString().trim() !== "");
  const hasWebSite = Boolean(data.webSite && data.webSite.trim() !== "");
  return hasEmail || hasPhone || hasWebSite;
};

export const hasContactSocialMediaData = (data: Partial<ContactSocialMedia> | undefined): boolean => {
  if (!data) return false;
  const hasFb = Boolean((data.facebook && data.facebook.trim() !== "") || data.hasFacebook);
  const hasWa = Boolean((data.whatsapp && data.whatsapp.toString().trim() !== "") || data.hasWhatsapp);
  const hasLi = Boolean((data.linkedin && data.linkedin.trim() !== "") || data.hasLinkedin);
  return hasFb || hasWa || hasLi;
};

export const hasFinancialSupportData = (data: Partial<FinancialSupport> | undefined): boolean => {
  if (!data) return false;
  const hasEmail = Boolean(data.email && data.email.trim() !== "");
  const hasPhone = Boolean(data.contactPhone && data.contactPhone.toString().trim() !== "");
  const hasWeb = Boolean(data.website && data.website.trim() !== "");
  const hasWa = Boolean((data.whatsapp && data.whatsapp.toString().trim() !== "") || data.hasWhatsapp);
  return hasEmail || hasPhone || hasWeb || hasWa || Boolean(data.hasFinancialSupport);
};

export const getSocialMediaData = (
  source: Partial<ContactSocialMedia> | undefined,
  fallback: Partial<ContactSocialMedia> | undefined
): ContactSocialMedia => ({
  facebook: source?.facebook || fallback?.facebook || "",
  hasFacebook: source?.hasFacebook ?? fallback?.hasFacebook ?? false,
  whatsapp: source?.whatsapp ?? fallback?.whatsapp ?? null,
  hasWhatsapp: source?.hasWhatsapp ?? fallback?.hasWhatsapp ?? false,
  linkedin: source?.linkedin || fallback?.linkedin || "",
  hasLinkedin: source?.hasLinkedin ?? fallback?.hasLinkedin ?? false,
});

export const getContactInfoData = (
  source: Partial<ContactInfo> | undefined,
  fallback: Partial<ContactInfo> | undefined
): ContactInfo => {
  const rawPrefix = source?.phone_code_country || fallback?.phone_code_country;
  return {
    contactEmail: source?.contactEmail || fallback?.contactEmail || "",
    phone_code_country: rawPrefix && rawPrefix !== "Prefijo" ? rawPrefix : "+57",
    contactPhone: source?.contactPhone ?? fallback?.contactPhone ?? null,
    webSite: source?.webSite || fallback?.webSite || "",
  };
};

// -------------------------------------------------------------
// Resolvers for Career inheritance from Institute and Faculty
// -------------------------------------------------------------

export const resolveInstituteContactInfo = (
  institute?: InstituteProps | null,
  careerIesContact?: Partial<ContactInfo>
): ContactInfo => {
  const rawPrefix =
    (institute?.contactInfoIES?.phone_code_country && institute.contactInfoIES.phone_code_country !== "Prefijo")
      ? institute.contactInfoIES.phone_code_country
      : (careerIesContact?.phone_code_country && careerIesContact.phone_code_country !== "Prefijo"
        ? careerIesContact.phone_code_country
        : "+57");

  return {
    contactEmail: institute?.contactInfoIES?.contactEmail || careerIesContact?.contactEmail || "",
    phone_code_country: rawPrefix,
    contactPhone: institute?.contactInfoIES?.contactPhone ?? careerIesContact?.contactPhone ?? null,
    webSite: institute?.webSite || institute?.contactInfoIES?.webSite || careerIesContact?.webSite || "",
  };
};

export const resolveFacultyContactInfo = (
  faculty?: facultyProps | null,
  iesFallback?: ContactInfo,
  careerFacultyContact?: Partial<ContactInfo>
): ContactInfo => {
  const facCustom = faculty?.contactInfo?.faculty;
  const facIes = faculty?.contactInfo?.ies;
  const hasFacCustom = hasContactInfoData(facCustom);

  const fallbackEmail = facIes?.contactEmail || iesFallback?.contactEmail || "";
  const fallbackPhone = facIes?.contactPhone ?? iesFallback?.contactPhone ?? null;
  const fallbackPrefix =
    facIes?.phone_code_country && facIes.phone_code_country !== "Prefijo"
      ? facIes.phone_code_country
      : (iesFallback?.phone_code_country && iesFallback.phone_code_country !== "Prefijo"
        ? iesFallback.phone_code_country
        : "+57");
  const fallbackWebSite =
    faculty?.webSiteFaculty || faculty?.webSite || facIes?.webSite || iesFallback?.webSite || "";

  if (hasFacCustom) {
    const rawPrefix = facCustom?.phone_code_country;
    return {
      contactEmail: facCustom?.contactEmail || fallbackEmail,
      phone_code_country: rawPrefix && rawPrefix !== "Prefijo" ? rawPrefix : fallbackPrefix,
      contactPhone: facCustom?.contactPhone ?? fallbackPhone,
      webSite: faculty?.webSiteFaculty || faculty?.webSite || facCustom?.webSite || fallbackWebSite,
    };
  }

  if (hasContactInfoData(careerFacultyContact)) {
    const rawPrefix = careerFacultyContact?.phone_code_country;
    return {
      contactEmail: careerFacultyContact?.contactEmail || fallbackEmail,
      phone_code_country: rawPrefix && rawPrefix !== "Prefijo" ? rawPrefix : fallbackPrefix,
      contactPhone: careerFacultyContact?.contactPhone ?? fallbackPhone,
      webSite: careerFacultyContact?.webSite || fallbackWebSite,
    };
  }

  return {
    contactEmail: fallbackEmail,
    phone_code_country: fallbackPrefix,
    contactPhone: fallbackPhone,
    webSite: fallbackWebSite,
  };
};

export const resolveCareerContactInfo = (
  career: CareerProps,
  facultyFallback?: ContactInfo,
  iesFallback?: ContactInfo
): ContactInfo => {
  const rawCareer = career.contactInfo?.career;
  const hasCareerCustom = hasContactInfoData(rawCareer);

  const fallbackPrefix =
    facultyFallback?.phone_code_country || iesFallback?.phone_code_country || "+57";

  if (hasCareerCustom) {
    const rawPrefix = rawCareer?.phone_code_country;
    return {
      contactEmail: rawCareer?.contactEmail || "",
      phone_code_country: rawPrefix && rawPrefix !== "Prefijo" ? rawPrefix : fallbackPrefix,
      contactPhone: rawCareer?.contactPhone ?? null,
      webSite: rawCareer?.webSite || career.webSiteCareer || career.webSite || "",
    };
  }

  const legacyEmail = career.contactEmail || "";
  const legacyPhone = career.contactPhone ?? null;
  const legacyPrefix =
    career.phone_code_country && career.phone_code_country !== "Prefijo"
      ? career.phone_code_country
      : fallbackPrefix;
  const legacyWebsite = career.webSiteCareer || career.webSite || "";

  if (legacyEmail || legacyPhone || legacyWebsite) {
    return {
      contactEmail: legacyEmail,
      phone_code_country: legacyPrefix,
      contactPhone: legacyPhone,
      webSite: legacyWebsite,
    };
  }

  return {
    contactEmail: "",
    phone_code_country: fallbackPrefix,
    contactPhone: null,
    webSite: "",
  };
};

export const resolveInstituteSocialMedia = (
  institute?: InstituteProps | null,
  careerIesSocial?: Partial<ContactSocialMedia>
): ContactSocialMedia => {
  const instSocial = institute?.contactSocialMediaIES;
  return {
    facebook: instSocial?.facebook || careerIesSocial?.facebook || "",
    hasFacebook: instSocial?.hasFacebook ?? careerIesSocial?.hasFacebook ?? false,
    whatsapp: instSocial?.whatsapp ?? careerIesSocial?.whatsapp ?? null,
    hasWhatsapp: instSocial?.hasWhatsapp ?? careerIesSocial?.hasWhatsapp ?? false,
    linkedin: instSocial?.linkedin || careerIesSocial?.linkedin || "",
    hasLinkedin: instSocial?.hasLinkedin ?? careerIesSocial?.hasLinkedin ?? false,
  };
};

export const resolveFacultySocialMedia = (
  faculty?: facultyProps | null,
  iesFallback?: ContactSocialMedia,
  careerFacultySocial?: Partial<ContactSocialMedia>
): ContactSocialMedia => {
  const facCustom = faculty?.contactSocialMedia?.faculty;
  const facIes = faculty?.contactSocialMedia?.ies;
  const hasFacCustom = hasContactSocialMediaData(facCustom);

  const fallbackFb = facIes?.facebook || iesFallback?.facebook || "";
  const fallbackHasFb = facIes?.hasFacebook ?? iesFallback?.hasFacebook ?? false;
  const fallbackWa = facIes?.whatsapp ?? iesFallback?.whatsapp ?? null;
  const fallbackHasWa = facIes?.hasWhatsapp ?? iesFallback?.hasWhatsapp ?? false;
  const fallbackLi = facIes?.linkedin || iesFallback?.linkedin || "";
  const fallbackHasLi = facIes?.hasLinkedin ?? iesFallback?.hasLinkedin ?? false;

  if (hasFacCustom) {
    return {
      facebook: facCustom?.facebook || fallbackFb,
      hasFacebook: facCustom?.hasFacebook ?? fallbackHasFb,
      whatsapp: facCustom?.whatsapp ?? fallbackWa,
      hasWhatsapp: facCustom?.hasWhatsapp ?? fallbackHasWa,
      linkedin: facCustom?.linkedin || fallbackLi,
      hasLinkedin: facCustom?.hasLinkedin ?? fallbackHasLi,
    };
  }

  if (hasContactSocialMediaData(careerFacultySocial)) {
    return {
      facebook: careerFacultySocial?.facebook || fallbackFb,
      hasFacebook: careerFacultySocial?.hasFacebook ?? fallbackHasFb,
      whatsapp: careerFacultySocial?.whatsapp ?? fallbackWa,
      hasWhatsapp: careerFacultySocial?.hasWhatsapp ?? fallbackHasWa,
      linkedin: careerFacultySocial?.linkedin || fallbackLi,
      hasLinkedin: careerFacultySocial?.hasLinkedin ?? fallbackHasLi,
    };
  }

  return {
    facebook: fallbackFb,
    hasFacebook: fallbackHasFb,
    whatsapp: fallbackWa,
    hasWhatsapp: fallbackHasWa,
    linkedin: fallbackLi,
    hasLinkedin: fallbackHasLi,
  };
};

export const resolveCareerSocialMedia = (
  career: CareerProps
): ContactSocialMedia => {
  const rawCareer = career.contactSocialMedia?.career;
  return {
    facebook: rawCareer?.facebook || "",
    hasFacebook: rawCareer?.hasFacebook ?? false,
    whatsapp: rawCareer?.whatsapp ?? null,
    hasWhatsapp: rawCareer?.hasWhatsapp ?? false,
    linkedin: rawCareer?.linkedin || "",
    hasLinkedin: rawCareer?.hasLinkedin ?? false,
  };
};

export const resolveInstituteFinancialSupport = (
  institute?: InstituteProps | null,
  careerIesFin?: Partial<FinancialSupport>
): FinancialSupport => {
  const instFin = institute?.financialSupportIES;
  const rawPrefix =
    (instFin?.phone_code_country && instFin.phone_code_country !== "Prefijo")
      ? instFin.phone_code_country
      : (careerIesFin?.phone_code_country && careerIesFin.phone_code_country !== "Prefijo"
        ? careerIesFin.phone_code_country
        : "+57");

  const hasData = hasFinancialSupportData(instFin) || hasFinancialSupportData(careerIesFin);

  return {
    website: instFin?.website || careerIesFin?.website || "",
    email: instFin?.email || careerIesFin?.email || "",
    phone_code_country: rawPrefix,
    contactPhone: instFin?.contactPhone ?? careerIesFin?.contactPhone ?? null,
    whatsapp: instFin?.whatsapp ?? careerIesFin?.whatsapp ?? null,
    hasWhatsapp: instFin?.hasWhatsapp ?? careerIesFin?.hasWhatsapp ?? false,
    hasFinancialSupport: hasData,
  };
};

export const resolveFacultyFinancialSupport = (
  faculty?: facultyProps | null,
  iesFallback?: FinancialSupport,
  careerFacultyFin?: Partial<FinancialSupport>
): FinancialSupport => {
  const facCustom = faculty?.financialSupport?.faculty;
  const facIes = faculty?.financialSupport?.ies;
  const hasFacCustom = hasFinancialSupportData(facCustom);
  const hasCareerFacultyFin = hasFinancialSupportData(careerFacultyFin);
  const hasFacIes = hasFinancialSupportData(facIes);

  const fallbackWeb = facIes?.website || iesFallback?.website || "";
  const fallbackEmail = facIes?.email || iesFallback?.email || "";
  const fallbackPrefix =
    facIes?.phone_code_country && facIes.phone_code_country !== "Prefijo"
      ? facIes.phone_code_country
      : (iesFallback?.phone_code_country && iesFallback.phone_code_country !== "Prefijo"
        ? iesFallback.phone_code_country
        : "+57");
  const fallbackPhone = facIes?.contactPhone ?? iesFallback?.contactPhone ?? null;
  const fallbackWa = facIes?.whatsapp ?? iesFallback?.whatsapp ?? null;
  const fallbackHasWa = facIes?.hasWhatsapp ?? iesFallback?.hasWhatsapp ?? false;
  const fallbackHasFin = hasFacIes || Boolean(iesFallback?.hasFinancialSupport);

  if (hasFacCustom) {
    const rawPrefix = facCustom?.phone_code_country;
    return {
      website: facCustom?.website || fallbackWeb,
      email: facCustom?.email || fallbackEmail,
      phone_code_country: rawPrefix && rawPrefix !== "Prefijo" ? rawPrefix : fallbackPrefix,
      contactPhone: facCustom?.contactPhone ?? fallbackPhone,
      whatsapp: facCustom?.whatsapp ?? fallbackWa,
      hasWhatsapp: facCustom?.hasWhatsapp ?? fallbackHasWa,
      hasFinancialSupport: true,
    };
  }

  if (hasCareerFacultyFin) {
    const rawPrefix = careerFacultyFin?.phone_code_country;
    return {
      website: careerFacultyFin?.website || fallbackWeb,
      email: careerFacultyFin?.email || fallbackEmail,
      phone_code_country: rawPrefix && rawPrefix !== "Prefijo" ? rawPrefix : fallbackPrefix,
      contactPhone: careerFacultyFin?.contactPhone ?? fallbackPhone,
      whatsapp: careerFacultyFin?.whatsapp ?? fallbackWa,
      hasWhatsapp: careerFacultyFin?.hasWhatsapp ?? fallbackHasWa,
      hasFinancialSupport: true,
    };
  }

  return {
    website: fallbackWeb,
    email: fallbackEmail,
    phone_code_country: fallbackPrefix,
    contactPhone: fallbackPhone,
    whatsapp: fallbackWa,
    hasWhatsapp: fallbackHasWa,
    hasFinancialSupport: fallbackHasFin,
  };
};

export const resolveCareerFinancialSupport = (
  career: CareerProps
): FinancialSupport => {
  const rawCareer = career.financialSupport?.career;
  const rawPrefix =
    rawCareer?.phone_code_country && rawCareer.phone_code_country !== "Prefijo"
      ? rawCareer.phone_code_country
      : "+57";

  const hasCareerData = hasFinancialSupportData(rawCareer);

  let resolvedHasFinancialSupport = false;
  if (typeof rawCareer?.hasFinancialSupport === "boolean") {
    resolvedHasFinancialSupport = rawCareer.hasFinancialSupport;
  } else if (typeof career.hasFinancialSupport === "boolean") {
    resolvedHasFinancialSupport = career.hasFinancialSupport;
  } else if (career.financialSupport?.default === "career" || hasCareerData) {
    resolvedHasFinancialSupport = true;
  }

  return {
    website: rawCareer?.website || "",
    email: rawCareer?.email || "",
    phone_code_country: rawPrefix,
    contactPhone: rawCareer?.contactPhone ?? null,
    whatsapp: rawCareer?.whatsapp ?? null,
    hasWhatsapp: rawCareer?.hasWhatsapp ?? false,
    hasFinancialSupport: resolvedHasFinancialSupport,
  };
};